import { useEffect, useRef, useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import { AnimatedInput } from "@/components/ui/animated-input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarClock,
  Clock3,
  Mail,
  Phone,
  UserRound,
  GraduationCap,
  Hash,
  BookOpen,
  Minus,
  Plus,
} from "lucide-react";

const empty = {
  responsible_name: "",
  student_name: "",
  age: "10",
  phone: "",
  email: "",
  level: "Iniciante",
  experience: "",
  notes: "",
};

const requiredValidation = {
  responsible_name: (value) =>
    value.trim() ? "" : "Informe o nome do responsável.",
  student_name: (value) =>
    value.trim() ? "" : "Informe o nome do aluno.",
  phone: (value) =>
    value.replace(/\D/g, "").length >= 10
      ? ""
      : "Informe um telefone com DDD.",
  email: (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
      ? ""
      : "Informe um e-mail válido.",
};

export default function ExperimentalClass() {
  const [form, setForm] = useState(empty);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsError, setSettingsError] = useState(false);
  const [confirmationUrl, setConfirmationUrl] = useState("");
  const formRef = useRef(null);

  const loadSettings = async () => {
    setSettingsError(false);
    setSettings(null);

    try {
      const { data } = await api.get("/public/experimental-settings");
      setSettings(data);
    } catch {
      setSettingsError(true);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const minimumAge = settings?.minimum_age ?? 5;

  const set = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const validateField = (key, value) => {
    if (key === "age") {
      const age = Number(value);
      return Number.isInteger(age) && age >= minimumAge && age <= 120
        ? ""
        : `Informe uma idade entre ${minimumAge} e 120 anos.`;
    }

    return requiredValidation[key]?.(value) || "";
  };

  const fieldState = (key) => {
    const value = String(form[key]);
    const error = validateField(key, value);

    return {
      error: touched[key] || submitted ? error : "",
      success: Boolean(value.trim()) && !error,
    };
  };

  const markTouched = (key) =>
    setTouched((current) => ({ ...current, [key]: true }));

  const stepAge = (amount) => {
    const current = Number(form.age);
    const next =
      Number.isInteger(current) && current >= minimumAge
        ? current + amount
        : minimumAge;

    set("age", String(Math.min(120, Math.max(minimumAge, next))));
  };

  const submit = async (event) => {
    event.preventDefault();

    if (loading || !settings?.accepting_requests) return;

    setSubmitted(true);

    const requiredFields = [
      "responsible_name",
      "student_name",
      "age",
      "phone",
      "email",
    ];

    const invalidField = requiredFields.find((key) =>
      validateField(key, String(form[key])),
    );

    if (invalidField) {
      formRef.current?.elements.namedItem(invalidField)?.focus();
      return;
    }

    setLoading(true);
    setConfirmationUrl("");

    try {
      const { data } = await api.post("/public/experimental", {
        ...form,
        age: Number(form.age),
        best_day: settings.day,
        best_time: settings.time,
        settings_version: settings.updated_at,
      });

      const lesson = data.lesson;

      toast.success(
        lesson.whatsapp
          ? "Solicitação enviada! Confirme pelo WhatsApp."
          : "Solicitação enviada! Aguarde nosso contato.",
      );

      const message =
        `*Nova Aula Experimental — GSX*\n\n` +
        `*Responsável:* ${form.responsible_name}\n` +
        `*Aluno:* ${form.student_name} (${form.age} anos)\n` +
        `*Telefone:* ${form.phone}\n` +
        `*Email:* ${form.email}\n` +
        `*Nível:* ${form.level}\n` +
        `*Experiência:* ${form.experience || "—"}\n` +
        `*Dia e horário:* ${lesson.day} às ${lesson.time}\n` +
        (lesson.location ? `*Local:* ${lesson.location}\n` : "") +
        `*Observações:* ${form.notes || "—"}`;

      if (lesson.whatsapp) {
        const whatsappUrl = `https://wa.me/${lesson.whatsapp}?text=${encodeURIComponent(message)}`;
        setConfirmationUrl(whatsappUrl);
        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      }

      setForm({
        ...empty,
        age: String(Math.max(10, minimumAge)),
      });
      setTouched({});
      setSubmitted(false);
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail) || "Erro ao enviar",
      );

      if (error.response?.status === 409) {
        await loadSettings();
      }
    } finally {
      setLoading(false);
    }
  };

  if (!settings) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-3xl px-6 py-16 text-center text-slate-300">
          {settingsError ? (
            <div role="alert">
              Não foi possível carregar as informações da aula.
              <Button onClick={loadSettings} className="ml-3">
                Tentar novamente
              </Button>
            </div>
          ) : (
            <p role="status">Carregando informações da aula…</p>
          )}
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="mb-9 text-center">
          {settings.badge && (
            <span className="inline-block rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs uppercase tracking-widest text-amber-300">
              {settings.badge}
            </span>
          )}

          <h1 className="mt-4 text-4xl font-semibold text-white sm:text-5xl">
            {settings.title || "Agende uma Aula Experimental"}
          </h1>

          <p className="mt-3 whitespace-pre-wrap text-slate-400">
            {settings.description ||
              "Um encontro de avaliação técnica com nossos treinadores GSX."}
          </p>

          <p className="mt-2 font-medium text-amber-300">
            Para alunos a partir de {minimumAge} anos.
          </p>
        </div>

        <section
          aria-label="Horário disponível"
          className="relative mb-6 overflow-hidden rounded-2xl border border-amber-500/30 bg-[linear-gradient(115deg,#123761_0%,#0D2A52_48%,#071A33_100%)] p-5 shadow-[0_18px_42px_rgba(0,0,0,0.2)] sm:p-7"
        >
          <div className="pointer-events-none absolute -right-12 -top-20 h-52 w-52 rounded-full bg-amber-400/10 blur-3xl" />

          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-amber-400/30 bg-amber-400/10 text-amber-300">
                <CalendarClock className="h-6 w-6" aria-hidden="true" />
              </div>

              <div>
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-300">
                  Horário disponível
                </span>
                <h2 className="mt-1 text-xl font-semibold text-white sm:text-2xl">
                  {settings.day} às {settings.time}
                </h2>
                {settings.location && (
                  <p className="mt-1 text-sm text-slate-300">
                    {settings.location}
                  </p>
                )}
                {settings.instructions && (
                  <p className="mt-1 whitespace-pre-wrap text-xs text-slate-400">
                    {settings.instructions}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 self-start rounded-full border border-white/15 bg-white/5 px-3 py-2 text-xs font-medium text-slate-200 sm:self-center">
              <Clock3
                className="h-4 w-4 text-amber-300"
                aria-hidden="true"
              />
              Horário definido pelo GSX
            </div>
          </div>
        </section>

        {confirmationUrl && (
          <p role="status" className="mb-6 text-amber-300">
            Solicitação recebida.{" "}
            <a
              href={confirmationUrl}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Abrir WhatsApp para confirmar
            </a>
          </p>
        )}

        {!settings.accepting_requests ? (
          <div
            role="status"
            className="rounded-2xl border border-white/10 bg-[#0D2A52]/70 p-6 text-slate-300"
          >
            As solicitações de aula experimental estão temporariamente pausadas.
          </div>
        ) : (
          <form
            ref={formRef}
            onSubmit={submit}
            noValidate
            className="space-y-5 px-1 sm:px-2"
            data-testid="experimental-form"
          >
            <fieldset disabled={loading} className="space-y-5">
              <div className="border-b border-white/10 pb-5">
                <h2 className="text-xl font-semibold text-white">
                  Dados para agendamento
                </h2>
                <p className="mt-1 text-sm text-slate-400">
                  Preencha os dados abaixo para solicitar a aula no horário indicado.
                </p>
              </div>

              <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
                <Field
                  label="Nome do responsável"
                  name="responsible_name"
                  icon={<UserRound className="h-4 w-4" />}
                  value={form.responsible_name}
                  onChange={(value) => set("responsible_name", value)}
                  onBlur={() => markTouched("responsible_name")}
                  {...fieldState("responsible_name")}
                  required
                  autoComplete="name"
                  testid="experimental-form-responsible"
                />

                <Field
                  label="Nome do aluno"
                  name="student_name"
                  icon={<GraduationCap className="h-4 w-4" />}
                  value={form.student_name}
                  onChange={(value) => set("student_name", value)}
                  onBlur={() => markTouched("student_name")}
                  {...fieldState("student_name")}
                  required
                  autoComplete="off"
                  testid="experimental-form-name"
                />

                <Field
                  label="Idade"
                  name="age"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  icon={<Hash className="h-4 w-4" />}
                  value={form.age}
                  onChange={(value) => {
                    if (/^\d{0,3}$/.test(value)) set("age", value);
                  }}
                  onBlur={() => markTouched("age")}
                  {...fieldState("age")}
                  required
                  testid="experimental-form-age"
                  endAdornment={
                    <div
                      className="mr-2 flex shrink-0 items-center gap-1"
                      aria-label="Ajustar idade"
                    >
                      <button
                        type="button"
                        onClick={() => stepAge(-1)}
                        disabled={
                          Number(form.age) <= minimumAge && form.age !== ""
                        }
                        aria-label="Diminuir idade"
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-amber-400/25 bg-amber-400/5 text-amber-300 transition-colors hover:border-amber-400/60 hover:bg-amber-400/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Minus className="h-4 w-4" aria-hidden="true" />
                      </button>

                      <button
                        type="button"
                        onClick={() => stepAge(1)}
                        disabled={Number(form.age) >= 120}
                        aria-label="Aumentar idade"
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-amber-400/25 bg-amber-400/5 text-amber-300 transition-colors hover:border-amber-400/60 hover:bg-amber-400/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  }
                />

                <Field
                  label="Telefone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  icon={<Phone className="h-4 w-4" />}
                  value={form.phone}
                  onChange={(value) => set("phone", value)}
                  onBlur={() => markTouched("phone")}
                  {...fieldState("phone")}
                  required
                  autoComplete="tel"
                  placeholder="(79) 99999-9999"
                  testid="experimental-form-phone"
                />

                <Field
                  label="E-mail"
                  name="email"
                  type="email"
                  icon={<Mail className="h-4 w-4" />}
                  value={form.email}
                  onChange={(value) => set("email", value)}
                  onBlur={() => markTouched("email")}
                  {...fieldState("email")}
                  required
                  autoComplete="email"
                  testid="experimental-form-email"
                />

                <div>
                  <Label
                    htmlFor="experimental-level"
                    className="mb-2 block text-sm font-medium text-slate-200"
                  >
                    Nível de xadrez
                  </Label>
                  <Select
                    value={form.level}
                    onValueChange={(value) => set("level", value)}
                  >
                    <SelectTrigger
                      id="experimental-level"
                      data-testid="experimental-form-level"
                      className="h-12 rounded-xl border-white/15 bg-[#0D2A52]/80 text-white hover:border-amber-400/35 focus:border-amber-400/70 focus:ring-amber-400/15"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["Iniciante", "Intermediário", "Avançado"].map(
                        (level) => (
                          <SelectItem key={level} value={level}>
                            {level}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-2">
                  <Field
                    label="Experiência anterior"
                    name="experience"
                    icon={<BookOpen className="h-4 w-4" />}
                    value={form.experience}
                    onChange={(value) => set("experience", value)}
                    success={Boolean(form.experience.trim())}
                    placeholder="Ex.: aulas, torneios ou prática em casa"
                    testid="experimental-form-experience"
                  />
                </div>
              </div>

              <div>
                <Label
                  htmlFor="experimental-notes"
                  className="mb-2 block text-sm font-medium text-slate-200"
                >
                  Observações{" "}
                  <span className="font-normal text-slate-500">(opcional)</span>
                </Label>
                <Textarea
                  id="experimental-notes"
                  rows={4}
                  data-testid="experimental-form-notes"
                  value={form.notes}
                  onChange={(event) => set("notes", event.target.value)}
                  placeholder="Conte algo que ajude a preparar a aula"
                  className="rounded-xl border-white/15 bg-[#0D2A52]/80 text-white placeholder:text-slate-500 focus:border-amber-400/70 focus:ring-amber-400/15"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="experimental-form-submit"
                className="h-12 w-full bg-amber-500 font-semibold text-[#071A33] hover:bg-amber-400"
              >
                {loading ? "Enviando…" : "Solicitar Aula Experimental"}
              </Button>
            </fieldset>
          </form>
        )}
      </main>
    </PublicLayout>
  );
}

function Field({ icon, testid, ...props }) {
  return (
    <AnimatedInput
      leftIcon={icon}
      reserveErrorLine
      data-testid={testid}
      {...props}
    />
  );
}
